import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

import type { KpiAxisConfiguration } from 'components/molecules/PackArchitecture/kpiAxisConfiguration.utils';
import { buildWhitespaceKpiAxisPreviewRequest } from 'components/pages/WhitespaceAnalysis/whitespaceGridRequest.helpers';
import { WHITESPACE_AXIS_PREVIEW_QUERY_OPTIONS } from 'hooks/whitespaceAxisPreviewQuery.helpers';
import { ppaUrls } from 'config/urls';

const KPI_AXIS_PREVIEW_DEBOUNCE_MS = 400;

type PreviewFetchSnapshot = Pick<
    KpiAxisConfiguration,
    'min' | 'max' | 'binningMethod' | 'binCount' | 'breakpoints'
>;

type WhitespaceGridBucket = {
    id?: string;
};

type WhitespaceGridColumnGroup = {
    data?: WhitespaceGridBucket[];
};

type WhitespaceGridKpiPreviewResponse = {
    data?: WhitespaceGridColumnGroup[];
};

const toFetchSnapshot = (config: KpiAxisConfiguration): PreviewFetchSnapshot => ({
    min: config.min,
    max: config.max,
    binningMethod: config.binningMethod,
    binCount: config.binCount,
    breakpoints: [...config.breakpoints],
});

const hasImmediateFetchChange = (
    previous: PreviewFetchSnapshot | null,
    current: PreviewFetchSnapshot,
) =>
    !previous ||
    previous.binningMethod !== current.binningMethod ||
    previous.breakpoints.join(',') !== current.breakpoints.join(',');

const buildWhitespaceKpiPreviewLabelsFromGrid = (
    response: WhitespaceGridKpiPreviewResponse,
): string[] => {
    const seen = new Set<string>();
    const labels: string[] = [];

    for (const group of response.data ?? []) {
        for (const bucket of group.data ?? []) {
            const label = bucket.id ?? '';
            if (!label || seen.has(label)) continue;
            seen.add(label);
            labels.push(label);
        }
    }

    return labels;
};

type UseWhitespaceKpiAxisConfigurationPreviewParams = {
    open: boolean;
    gridRequest: Record<string, unknown> | null;
    previewConfig: KpiAxisConfiguration;
    pauseFetch?: boolean;
};

export const useWhitespaceKpiAxisConfigurationPreview = ({
    open,
    gridRequest,
    previewConfig,
    pauseFetch = false,
}: UseWhitespaceKpiAxisConfigurationPreviewParams) => {
    const [debouncedConfig, setDebouncedConfig] = useState(previewConfig);
    const previousSnapshotRef = useRef<PreviewFetchSnapshot | null>(null);

    useEffect(() => {
        if (!open) {
            previousSnapshotRef.current = null;
            return undefined;
        }

        const currentSnapshot = toFetchSnapshot(previewConfig);
        const fetchDelay = hasImmediateFetchChange(previousSnapshotRef.current, currentSnapshot)
            ? 0
            : KPI_AXIS_PREVIEW_DEBOUNCE_MS;
        previousSnapshotRef.current = currentSnapshot;

        const timer = window.setTimeout(() => {
            setDebouncedConfig(previewConfig);
        }, fetchDelay);

        return () => {
            window.clearTimeout(timer);
        };
    }, [open, previewConfig]);

    const previewRequest = useMemo(() => {
        if (!gridRequest || !open) {
            return null;
        }
        return buildWhitespaceKpiAxisPreviewRequest(gridRequest, debouncedConfig);
    }, [debouncedConfig, gridRequest, open]);

    const previewRequestKey = previewRequest ? JSON.stringify(previewRequest) : null;
    const hasValidRange = debouncedConfig.min < debouncedConfig.max;
    const hasValidBinCount =
        debouncedConfig.binningMethod === 'uniform'
            ? debouncedConfig.binCount > 0
            : debouncedConfig.breakpoints.length > 0;
    const queryEnabled = open && Boolean(previewRequestKey) && hasValidRange && hasValidBinCount && !pauseFetch;

    const { data, isFetching, isPending } = useQuery<string[]>({
        queryKey: ['whitespace-kpi-axis-preview', previewRequestKey],
        queryFn: async ({ signal }) => {
            const response = await axios.post<WhitespaceGridKpiPreviewResponse>(
                ppaUrls.getWhitespaceAnalysisGridUrl,
                previewRequest,
                { signal },
            );
            return buildWhitespaceKpiPreviewLabelsFromGrid(response.data);
        },
        enabled: queryEnabled,
        ...WHITESPACE_AXIS_PREVIEW_QUERY_OPTIONS,
    });

    const isConfigDebouncing =
        open &&
        JSON.stringify(toFetchSnapshot(previewConfig)) !== JSON.stringify(toFetchSnapshot(debouncedConfig));

    const isLoading = queryEnabled && (isPending || isFetching || isConfigDebouncing);

    return {
        previewLabels: open ? (data ?? []) : [],
        isLoading,
    };
};
